import { useEffect, useState } from "react";
import { useUpdateGasStation } from "@/lib/queries";
import type { GasStation } from "@/types/database";
import { Button } from "@/components/ui/Button";
import { Field, Input } from "@/components/ui/Input";
import { Modal } from "@/components/ui/Modal";
import { imageSrc } from "@/lib/utils";
import { resolveStationLogo } from "@/lib/station-logos";

const ACCEPTED_LOGO_TYPES = "image/png,image/jpeg,image/jpg,image/webp,image/svg+xml";

function formFromStation(station: GasStation) {
  return {
    name: station.name,
    address: station.address,
    parish: station.parish ?? "",
    fuelType: station.fuelType ?? "",
    slug: station.slug ?? "",
    fuelPrice_87: station.fuelPrice_87?.toString() ?? "",
    fuelPrice_90: station.fuelPrice_90?.toString() ?? "",
    fuelPrice_Diesel: station.fuelPrice_Diesel?.toString() ?? "",
    fuelPrice_ULSD: station.fuelPrice_ULSD?.toString() ?? "",
    coordinatesLat: station.coordinatesLat?.toString() ?? "",
    coordinatesLong: station.coordinatesLong?.toString() ?? "",
  };
}

function parseNumber(value: string): number | null {
  if (!value.trim()) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export function EditGasStationModal({
  station,
  open,
  onClose,
}: {
  station: GasStation;
  open: boolean;
  onClose: () => void;
}) {
  const updateStation = useUpdateGasStation();
  const [form, setForm] = useState(() => formFromStation(station));
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const saving = updateStation.isPending;
  const existingLogoUrl = resolveStationLogo(station);

  useEffect(() => {
    if (!open) return;
    setForm(formFromStation(station));
    setLogoFile(null);
    setLogoPreviewUrl(null);
    setError(null);
  }, [open, station]);

  useEffect(() => {
    if (!logoFile) {
      setLogoPreviewUrl(null);
      return;
    }

    const previewUrl = URL.createObjectURL(logoFile);
    setLogoPreviewUrl(previewUrl);
    return () => URL.revokeObjectURL(previewUrl);
  }, [logoFile]);

  function closeModal() {
    if (saving) return;
    onClose();
  }

  function handleLogoChange(file: File | null) {
    if (!file) {
      setLogoFile(null);
      return;
    }

    if (!file.type.startsWith("image/")) {
      setError("Logo must be an image file.");
      return;
    }

    setError(null);
    setLogoFile(file);
  }

  async function handleSave() {
    const name = form.name.trim();
    const address = form.address.trim();
    if (!name) {
      setError("Station name is required.");
      return;
    }
    if (!address) {
      setError("Address is required.");
      return;
    }

    setError(null);
    try {
      await updateStation.mutateAsync({
        id: station.id,
        stationLogo: station.stationLogo,
        logoFile,
        updates: {
          name,
          address,
          parish: form.parish.trim() || null,
          fuelType: form.fuelType.trim() || null,
          slug: form.slug.trim() || null,
          fuelPrice_87: parseNumber(form.fuelPrice_87),
          fuelPrice_90: parseNumber(form.fuelPrice_90),
          fuelPrice_Diesel: parseNumber(form.fuelPrice_Diesel),
          fuelPrice_ULSD: parseNumber(form.fuelPrice_ULSD),
          coordinatesLat: parseNumber(form.coordinatesLat),
          coordinatesLong: parseNumber(form.coordinatesLong),
        },
      });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to update station");
    }
  }

  const previewSrc = logoPreviewUrl ?? imageSrc(station.stationLogo) ?? existingLogoUrl;

  return (
    <Modal
      open={open}
      onClose={closeModal}
      title="Edit gas station"
      className="max-w-lg"
      footer={
        <>
          <Button variant="ghost" onClick={closeModal} disabled={saving}>
            Cancel
          </Button>
          <Button variant="brand" onClick={handleSave} disabled={saving || !form.name.trim() || !form.address.trim()}>
            {saving ? "Saving…" : "Save changes"}
          </Button>
        </>
      }
    >
      {error ? <p className="mb-4 rounded-2xl bg-red-50 px-4 py-2.5 text-sm text-red-600">{error}</p> : null}
      <div className="space-y-4 text-foreground">
        <Field label="Station name">
          <Input
            value={form.name}
            onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))}
            placeholder="e.g. Rubis"
            autoFocus
          />
        </Field>

        <Field label="Address">
          <Input
            value={form.address}
            onChange={(e) => setForm((current) => ({ ...current, address: e.target.value }))}
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Parish">
            <Input
              value={form.parish}
              onChange={(e) => setForm((current) => ({ ...current, parish: e.target.value }))}
            />
          </Field>
          <Field label="Fuel type">
            <Input
              value={form.fuelType}
              onChange={(e) => setForm((current) => ({ ...current, fuelType: e.target.value }))}
              placeholder="e.g. Gasoline"
            />
          </Field>
        </div>

        <Field label="Station logo">
          <div className="space-y-3">
            {previewSrc ? (
              <div className="flex h-24 items-center justify-center rounded-2xl bg-canvas p-3">
                <img src={previewSrc} alt="Logo preview" className="max-h-full max-w-full object-contain" />
              </div>
            ) : (
              <div className="flex h-24 items-center justify-center rounded-2xl bg-canvas p-3 text-sm text-muted">
                No logo uploaded
              </div>
            )}
            <Input
              type="file"
              accept={ACCEPTED_LOGO_TYPES}
              onChange={(e) => handleLogoChange(e.target.files?.[0] ?? null)}
            />
            <p className="text-xs text-muted">
              Uploads to GasStations / stationLogos / {station.id} / file and saves the public URL to stationLogo.
            </p>
            {imageSrc(station.stationLogo) ? (
              <p className="text-xs text-muted">Current URL: {station.stationLogo}</p>
            ) : null}
          </div>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="E-10 87">
            <Input
              type="number"
              step="0.1"
              value={form.fuelPrice_87}
              onChange={(e) => setForm((current) => ({ ...current, fuelPrice_87: e.target.value }))}
            />
          </Field>
          <Field label="E-10 90">
            <Input
              type="number"
              step="0.1"
              value={form.fuelPrice_90}
              onChange={(e) => setForm((current) => ({ ...current, fuelPrice_90: e.target.value }))}
            />
          </Field>
          <Field label="Diesel">
            <Input
              type="number"
              step="0.1"
              value={form.fuelPrice_Diesel}
              onChange={(e) => setForm((current) => ({ ...current, fuelPrice_Diesel: e.target.value }))}
            />
          </Field>
          <Field label="ULSD">
            <Input
              type="number"
              step="0.1"
              value={form.fuelPrice_ULSD}
              onChange={(e) => setForm((current) => ({ ...current, fuelPrice_ULSD: e.target.value }))}
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Latitude">
            <Input
              type="number"
              step="any"
              value={form.coordinatesLat}
              onChange={(e) => setForm((current) => ({ ...current, coordinatesLat: e.target.value }))}
            />
          </Field>
          <Field label="Longitude">
            <Input
              type="number"
              step="any"
              value={form.coordinatesLong}
              onChange={(e) => setForm((current) => ({ ...current, coordinatesLong: e.target.value }))}
            />
          </Field>
        </div>

        <Field label="Slug (optional)">
          <Input
            value={form.slug}
            onChange={(e) => setForm((current) => ({ ...current, slug: e.target.value }))}
          />
        </Field>
      </div>
    </Modal>
  );
}
